import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Upload, CheckCircle2, AlertCircle } from "lucide-react";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";

export default function AdminImports() {
    const [uploading, setUploading] = useState(false);
    const [session, setSession] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const [confirming, setConfirming] = useState(false);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        setSession(null);
        setSelected(new Set());
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/admin/imports/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 300000,
            });
            setSession(data);
            // Preselect all rows that don't have dedup
            const initial = new Set();
            data.rows.forEach((r, i) => {
                if (!r._dedup_candidate_id && r.name && r.producer) initial.add(i);
            });
            setSelected(initial);
            toast.success(`Analizzate ${data.count} righe`);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore upload");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const toggle = (i) => {
        const next = new Set(selected);
        if (next.has(i)) next.delete(i); else next.add(i);
        setSelected(next);
    };

    const confirm = async () => {
        if (!session) return;
        setConfirming(true);
        try {
            const { data } = await api.post(`/admin/imports/${session.session_id}/confirm`, {
                row_indexes: Array.from(selected).sort((a, b) => a - b),
            });
            toast.success(`${data.created_count} vini creati, ${data.merge_proposals?.length || 0} proposte di merge`);
            setSession(null);
            setSelected(new Set());
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore conferma");
        } finally {
            setConfirming(false);
        }
    };

    return (
        <div data-testid="admin-imports-page">
            <h1 className="text-3xl mb-2">Import Carta Vini</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-6 max-w-2xl">
                Carica un file CSV o PDF con l'elenco dei vini. L'AI Gemini estrarrà e normalizzerà
                le righe. Rivedi l'anteprima, deseleziona ciò che non vuoi importare e conferma.
                I vini creati avranno source <code>file</code> e stock vuoto.
            </p>

            <div className="bg-white border-2 border-dashed border-[hsl(var(--border))] rounded-sm p-8 text-center mb-8">
                <input
                    id="import-file-input"
                    type="file"
                    accept=".csv,.pdf,application/pdf,text/csv"
                    onChange={upload}
                    className="hidden"
                    data-testid="import-file-input"
                />
                <label htmlFor="import-file-input" className="cursor-pointer inline-flex flex-col items-center gap-3">
                    <Upload className="w-10 h-10 text-[hsl(var(--muted-foreground))]" strokeWidth={1.2} />
                    <div className="text-base">
                        {uploading ? "Analisi in corso…" : "Trascina qui o clicca per caricare CSV / PDF"}
                    </div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                        Il parsing AI può richiedere fino a 30 secondi.
                    </div>
                </label>
            </div>

            {session && (
                <div data-testid="import-preview">
                    <div className="mb-4 flex items-baseline justify-between">
                        <div>
                            <h2 className="text-2xl">Anteprima · {session.source.toUpperCase()}</h2>
                            <p className="text-sm text-[hsl(var(--muted-foreground))]">
                                {selected.size} di {session.rows.length} righe selezionate
                            </p>
                        </div>
                        <button
                            onClick={confirm}
                            disabled={confirming || selected.size === 0}
                            className="co-btn-primary"
                            data-testid="import-confirm-btn"
                        >
                            {confirming ? "Creazione…" : `Conferma ${selected.size} vini`}
                        </button>
                    </div>

                    <div className="bg-white border border-[hsl(var(--border))] rounded-sm overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="w-10"></TableHead>
                                    <TableHead>Nome</TableHead>
                                    <TableHead>Produttore</TableHead>
                                    <TableHead>Annata</TableHead>
                                    <TableHead>Regione</TableHead>
                                    <TableHead>Tipo</TableHead>
                                    <TableHead>Prezzo €</TableHead>
                                    <TableHead>Dedup</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {session.rows.map((r, i) => (
                                    <TableRow key={i} data-testid={`import-row-${i}`}>
                                        <TableCell>
                                            <Checkbox
                                                checked={selected.has(i)}
                                                onCheckedChange={() => toggle(i)}
                                                data-testid={`import-row-checkbox-${i}`}
                                            />
                                        </TableCell>
                                        <TableCell className="font-medium">{r.name || <em>—</em>}</TableCell>
                                        <TableCell>{r.producer || <em>—</em>}</TableCell>
                                        <TableCell>{r.vintage || "—"}</TableCell>
                                        <TableCell>{r.region || "—"}</TableCell>
                                        <TableCell>{r.type || "—"}</TableCell>
                                        <TableCell>{r.base_price_public ? `€ ${Number(r.base_price_public).toFixed(2)}` : "—"}</TableCell>
                                        <TableCell>
                                            {r._dedup_candidate_id ? (
                                                <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--accent))]" title="Sarà creata proposta di merge">
                                                    <AlertCircle className="w-3.5 h-3.5" strokeWidth={1.5} /> merge
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))]">
                                                    <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.5} /> nuovo
                                                </span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}
        </div>
    );
}
